import Currency from 'currency.js';
import React, {useEffect, useReducer, useState} from 'react';
import {getLocalState, setLocalState} from 'utils/localStorage';
import {CartContext} from './cart.context';
import moment from "moment";
import {AttributeInput, CouponTypeEnum, DimensionsInput, ProductTypeEnum} from "API";
import {useApolloClient} from "@apollo/client";
import gql from "graphql-tag";
import {getCoupon} from "graphql/queries";

import {ProductStatusEnum} from "@bb-rentals/shop/API";
import {Coupon, Media} from "@bb-rentals/shop/interfaces";

const initialState = {
    isOpen: false,
};

function reducer(cartState: any, action: any) {
    switch (action.type) {
        case 'TOGGLE':
            return {
                ...cartState,
                isOpen: !cartState.isOpen,
            };
        default:
            return cartState;
    }
}


export type Product = {
    company: string,
    id: string,
    comloc: string,
    location: string,
    type: ProductTypeEnum,
    sku?: string | null,
    name?: string | null,
    slug?: string | null,
    description?: string | null,
    attributes?: Array<AttributeInput | null> | null,
    status?: ProductStatusEnum | null,
    deposit?: number | null,
    price?: number | null,
    cost?: number | null,
    dimensions?: DimensionsInput | null,
    image: Media | null,
    media: {
        __typename?: "ModelResourceMediaConnection",
        items: Array<{ media: Media, default?: boolean } | null> | null,
        nextToken: string | null,
    } | null,
};


export interface CartProduct extends Product {
    id: string;
    total: number;
    subTotal: number;
    discountTotal: number;
    depositTotal: number;
    qty: number;
}


export type Cart = {
    orderStartTime?: string,
    orderEndTime?: string,
    tripDays: number,
    tripDiscountMultiplier: number,
    products: CartProduct[],
    coupons: Coupon[],
    total: number,
    subTotal: number,
    discountTotal: number,
    depositTotal: number,
}

function getInitialCart(): Cart {
    return {
        orderStartTime: null,
        orderEndTime: null,
        tripDays: 0,
        tripDiscountMultiplier: 1,
        products: [],
        total: 0,
        subTotal: 0,
        depositTotal: 0,
        discountTotal: 0,
        coupons: [],
    };
}

export interface LineTotal {
    total: number,
    subTotal: number,
    discountTotal: number,
    depositTotal: number,
}

function getTripDiscount(tripDays) {
    if (tripDays >= 4 && tripDays <= 7) {
        return 0.9;
    }
    if (tripDays >= 8 && tripDays <= 14) {
        return 0.8;
    }
    if (tripDays >= 15 && tripDays <= 20) {
        return 0.7;
    }
    if (tripDays >= 21 && tripDays <= 30) {
        return 0.65;
    }
    if (tripDays >= 31 && tripDays <= 40) {
        return 0.60;
    } else return 1;
}

const CartProvider = (props: any) => {

    const [cartState, dispatch] = useReducer(reducer, initialState);
    const [cart, setCart] = useState(getLocalState('bbcart') || getInitialCart() as Cart);
    const client = useApolloClient();


    useEffect(() => {
        setLocalState('bbcart', cart);
        if (process.env.NODE_ENV !== 'production') console.log('cart', cart)
    }, [cart]);


    function toggle() {
        dispatch({type: 'TOGGLE',});
    }

    function getProduct(id) {
        return cart.products.find(p => p.id === id);
    }

    function getProductPrice(price, type = null) {
        if (type === ProductTypeEnum.RETAIL_ITEM) {
            return price;
        }
        const tripDiscount = getTripDiscount(cart.tripDays);
        return Number((price * tripDiscount).toFixed(2));
    }

    function getProductTripTotal(product, qty = 1): LineTotal {
        return calculateLineTotal(cart, product, qty);
    }


    function setShippingOption(product, qty = 1): LineTotal {
        return calculateLineTotal(cart, product, qty);
    }


    const calculateLineTotal = (cart, product, qty = 1): LineTotal => {
        // total = subtotal + discountTotal
        const totals = {
            total: 0,
            subTotal: 0,
            discountTotal: 0,
            depositTotal: product?.deposit * qty || 0,
        };

        let daysMultiple = cart.tripDays;
        if (product.type === ProductTypeEnum.RETAIL_ITEM) {
            // this is a retail item so dont calculate the price per day, only
            // the quantity price
            daysMultiple = 1;
        }
        totals.subTotal = Number(Currency(qty).multiply(getProductPrice(product.price,product.type)).multiply(daysMultiple).value);

        if (cart.coupons.length > 0) {
            /*
             * Calculate the coupon discounts
             */
            const discount = cart.coupons
                .reduce((acc, coupon) => {
                    let discount = 0;
                    if (coupon.type === CouponTypeEnum.PERCENTAGE) {
                        discount = (coupon.value / 100) * acc.netTotal;
                    }
                    if (coupon.type === CouponTypeEnum.FIXED) {
                        discount = coupon.value;
                    }

                    return {
                        netTotal: acc.netTotal - discount,
                        discountTotal: acc.discountTotal + discount
                    }
                }, {
                    netTotal: totals.subTotal,
                    discountTotal: 0,
                });

            totals.discountTotal = discount.discountTotal;
            totals.total = totals.subTotal - totals.discountTotal;

        } else {
            totals.total = totals.subTotal;
            totals.discountTotal = 0;
        }
        return totals;
    }
    /*
     * Calculate the price of the items for the duration of the trip
     */
    const calculateCart = (cart: Cart): Cart => {

        const newProducts = [];
        const {products} = cart;
        const totals = {
            total: 0,
            subTotal: 0,
            discountTotal: 0,
            depositTotal: 0,
        };
        for (let product of products) {
            const {total, subTotal, discountTotal, depositTotal} = calculateLineTotal(cart, product, product.qty);
            totals.total += total;
            totals.subTotal += subTotal;
            totals.discountTotal += discountTotal;
            totals.depositTotal += depositTotal;
            newProducts.push({...product, total, subTotal, discountTotal, depositTotal});
        }

        return {
            ...cart,
            products: newProducts,
            ...totals
        };
    };


    const add = (item: CartProduct) => {
        setCart((cart) => {
            if (cart.products.length > 0) {
                const index = cart.products.findIndex(product => product.id === item.id);
                if (index !== -1) {
                    // if product already available in the cart
                    const product: CartProduct = cart.products[index];
                    const qty = (product.qty || 0) + 1;
                    const totals = calculateLineTotal(cart, item, qty);

                    cart.products[index] = {
                        ...product,
                        ...item,
                        qty,
                        ...totals,
                    }; // just increase the quantity
                } else {
                    // if this product is not available in the cart
                    const totals = calculateLineTotal(cart, item, 1);
                    cart.products.push({
                        ...item,
                        qty: 1,
                        ...totals,
                    });
                }
            } else {
                // if the cart is empty
                const totals = calculateLineTotal(cart, item, 1);
                cart.products.push({
                    ...item,
                    qty: 1,
                    ...totals,
                });
            }


            return calculateCart(cart);
        });
    };

    async function addCoupon(id: string) {
        return client.query({
            query: gql(getCoupon),
            context: {clientName: 'authed'},
            variables: {
                company: 'scuttle',
                id
            }
        }).then(({data: {getCoupon}}) => {
            if (getCoupon) {
                setCart((cart) => {
                    cart.coupons.push(getCoupon);
                    return calculateCart(cart);
                });
            } else {
                throw new Error(`Invalid coupon`);
            }
        });
    };

    const removeCoupon = (id) => {
        setCart((cart) => {
            cart.coupons = cart.coupons.filter(c => c.id !== id);
            return calculateCart(cart);
        });
    };

    const update = (itemId: any, qty: number) => {
        setCart((cart) => {
            const {products} = cart;
            const index = products.findIndex(product => product.id === itemId);
            if (qty < 1 && index > -1) {
                // delete if quantity, 0
                products.splice(index, 1);
            } else {
                // update quantity
                const product = products[index];
                const totals = calculateLineTotal(cart, product, qty);

                products[index] = {
                    ...product,
                    ...totals,
                    qty,
                };
            }

            return calculateCart(cart);
        });
    };

    const clearCart = () => {
        setCart(getInitialCart());
    };
    const setOrderStartTime = (date: string) => {
        setCart((cart) => {
            cart.orderStartTime = date;
            if (moment(cart.orderEndTime).isBefore(moment(cart.orderStartTime))) {
                cart.orderEndTime = date;
            }
            if (cart.orderEndTime) {
                cart.tripDays = moment(cart.orderEndTime).diff(moment(cart.orderStartTime), 'days') + 1;
                cart.tripDiscountMultiplier = getTripDiscount(cart.tripDays);
            }
            return calculateCart(cart);
        });
    };
    const setOrderEndTime = (date: string) => {
        setCart((cart) => {
            cart.orderEndTime = date;
            if (moment(cart.orderEndTime).isBefore(moment(cart.orderStartTime))) {
                cart.orderStartTime = date;
            }
            cart.tripDays = moment(cart.orderEndTime).diff(moment(cart.orderStartTime), 'days') + 1;
            cart.tripDiscountMultiplier = getTripDiscount(cart.tripDays);
            return calculateCart(cart);
        });
    };


    return (
        <CartContext.Provider
            value={{
                cartState,
                dispatch,
                toggle,
                cart,
                add,
                getProductPrice,
                getProduct,
                update,
                addCoupon,
                removeCoupon,
                setOrderStartTime,
                setOrderEndTime,
                clearCart,
                getProductTripTotal,
            }}
        >
            {props.children}
        </CartContext.Provider>
    );
};
export default CartProvider;